import {
  getPnlFormattedHoursMinutes,
  ONE_MINUTE_OF_MS,
} from "../../../../../utils/timeUtils";
import styled from "styled-components/macro";
import { medText } from "../../../../../constants/colors";
import { Divider } from "@mui/material";
import { TrendChartCenterMetricStyles } from "./TrendChartCenterMetricStyles";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useShouldUseDataDumpsForTrendChartCenterMetric } from "utils/customHooks/whichServerHooks";
import {
  RollupWithTagsAndCountsAndRange,
  TimeRangeFinite,
  TrendsPageFilterStateType,
} from "types/types";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useRangeFilteredByShift } from "../../TrendChartBarsGetter";
import { useMemo } from "react";
import {
  useFindDataDumpForTrendChart,
  useGetPageFromPath,
  useTrendsPageFilterState,
} from "utils/customHooks/genericHooks";
import { calcMetric } from "utils/metrics";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { getIsCurrent } from "../../trendChartUtils";
import { useMetricsForWorkSchedule } from "components/DataGetter/FlightConfig/useFlightConfig";

const TrendChartCenterMetric = ({
  rollupMetric,
  barRange,
}: {
  rollupMetric?: RollupWithTagsAndCountsAndRange;
  barRange: TimeRangeFinite;
}) => {
  const isStorybook = useIsStorybook();

  const rollupMetricOrShiftRange = useRangeFilteredByShift(barRange);

  const rollupMetricPossiblyFilteredByShift = useMemo(
    () => ({ ...rollupMetric, range: rollupMetricOrShiftRange }),
    [rollupMetric, rollupMetricOrShiftRange]
  );

  const now = Date.now();

  const { preferredMetric } = useSettings();

  const dataDumpForThisBar = useFindDataDumpForTrendChart({
    geToFind: rollupMetricPossiblyFilteredByShift.range.ge,
  });

  const shouldUseDataDumpsForTrendChartCenterMetric =
    useShouldUseDataDumpsForTrendChartCenterMetric();

  const metricValue = calcMetric({
    rollupMetric,
    dataDumpKey: preferredMetric,
    dataDumpsObjForRange: dataDumpForThisBar,
    isMidgardDDAEnabled: shouldUseDataDumpsForTrendChartCenterMetric,
  });
  const isCurrentBar = isStorybook
    ? false
    : getIsCurrent({
        now,
        rangeGe: barRange.ge,
        rangeLt: barRange.lt,
      });

  // only animate once

  const { isTrendsPage } = useGetPageFromPath();
  const {
    isTrendsPageFilteredByTag,
    isTrendsPageFilteredByPnlCategory,
    trendsPageFilterState,
  } = useTrendsPageFilterState();

  const isTrendsPageFilteredByTagOrCategory = Boolean(
    isTrendsPage &&
      (isTrendsPageFilteredByTag || isTrendsPageFilteredByPnlCategory)
  );

  const timeMs =
    !rollupMetric ||
    /* ? sometimes it's this shape { range: {}} */
    Object.keys(rollupMetric).length <= 1
      ? 0
      : isTrendsPageFilteredByTagOrCategory
        ? isTrendsPageFilteredByTag
          ? getFilteredTimeMsForTag(rollupMetric, trendsPageFilterState)
          : getFilteredTimeMsForCategory(rollupMetric, trendsPageFilterState)
        : null;

  const shouldShowMetric =
    // show percent on pages other than trends page
    !isTrendsPage ||
    // hide percent when filtered by tag
    !isTrendsPageFilteredByTagOrCategory;
  // show time when filtered by tag
  const shouldShowTimeMs = isTrendsPageFilteredByTagOrCategory;

  const showTimeMs = Boolean(shouldShowTimeMs && (timeMs || timeMs === 0));
  const showMetric = shouldShowMetric && (metricValue || metricValue === 0);
  const showNoPercentIndicator = !showTimeMs && !showMetric;

  const numRowsVisible =
    Number(showMetric) +
    Number(showTimeMs) +
    Number(!showTimeMs && !showMetric);

  const metricValueDisplay =
    metricValue || metricValue === 0 ? metricValue : null;
  const metrics = useMetricsForWorkSchedule();
  const metric = metrics.find(m => m.dataDumpKey === preferredMetric);
  const metricUnits = metric?.units ?? null;

  return (
    <TrendChartCenterMetricStyles
      className={`label metricLabel${isCurrentBar ? " callout" : ""}`}
      $numRowsVisible={numRowsVisible}
    >
      {showMetric ? (
        <div
          className="metricPercent"
          data-server-names={[
            shouldUseDataDumpsForTrendChartCenterMetric
              ? SERVER_NAMES.midgardDataDumpsTrendChart
              : SERVER_NAMES.askrAggregates,
          ]}
        >
          {(metricValueDisplay || 0).toFixed(0)}
          {metricUnits === "%" ? (
            <span className="percent">{metricUnits}</span>
          ) : null}
          {metricUnits && metricUnits !== "%" ? (
            <div className="units">{metricUnits}</div>
          ) : null}
        </div>
      ) : null}
      {showMetric && showTimeMs ? <Divider /> : null}
      {showTimeMs ? <HoursMinutes timeMs={timeMs || 0} /> : null}
      {showNoPercentIndicator ? (
        <div className="noPercentIndicator">--</div>
      ) : null}
    </TrendChartCenterMetricStyles>
  );
};
export default TrendChartCenterMetric;

const HoursMinutesStyles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  :not(span).minutes {
    color: ${medText};
  }
  span.minutes {
    font-size: 0.6em;
  }
`;
function HoursMinutes({ timeMs }: { timeMs: number }) {
  const { hours, minutes } = getPnlFormattedHoursMinutes(timeMs);
  return (
    <HoursMinutesStyles>
      {Number(hours) > 10 ? (
        // hide minutes when > 10 hours
        <div className="hoursAndMinutes">{hours}h</div>
      ) : Number(hours) > 0 ? (
        <div className="hoursAndMinutes">
          {hours}h<span className="minutes">{minutes}m</span>
        </div>
      ) : (
        <div className="minutes">
          {Math.round(timeMs / ONE_MINUTE_OF_MS)}
          <span className="minutes">min</span>
        </div>
      )}
    </HoursMinutesStyles>
  );
}

function getFilteredTimeMsForTag(
  rollupMetric: RollupWithTagsAndCountsAndRange,
  trendsPageFilterState: TrendsPageFilterStateType
) {
  return Object.entries(rollupMetric.tags).reduce(
    (acc, [pnlCategory, tagsObj]) => {
      if (pnlCategory === trendsPageFilterState.pnlCategory) {
        if (
          // if filtering by tagless categories like "Producing" or "Unknown",
          // just use the aggregate value
          pnlCategory.toLowerCase() === trendsPageFilterState.tag.toLowerCase()
        ) {
          acc += rollupMetric[pnlCategory];
        } else {
          // sum the matching tag times
          Object.entries(tagsObj).forEach(([tagName, totalDurationMs]) => {
            if (tagName === trendsPageFilterState.tag) {
              acc += totalDurationMs;
            }
          });
        }
      }
      return acc;
    },
    0
  );
}
function getFilteredTimeMsForCategory(
  rollupMetric: RollupWithTagsAndCountsAndRange,
  trendsPageFilterState: TrendsPageFilterStateType
) {
  return Object.entries(rollupMetric).reduce(
    // rollupMetric entries could be e.g. "producing: 12691276" or "range: {...}" etc
    (acc, [pnlCategoryOrOther, totalDurationMsOrOther]) => {
      if (pnlCategoryOrOther === trendsPageFilterState.pnlCategory) {
        acc += totalDurationMsOrOther as number;
      }
      return acc;
    },
    0
  );
}
